import { Col, Collapse, List, Row, Skeleton, Typography } from "antd"
import { calculatePerInvoiceAmounts, calculateSalesTax, calculateTotalPrice, updatePaymentScheduleAmounts, Workarea } from "./proposalsModel"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { roundToPrecision } from "../../../helpers/math"
import VSpace from "../../common/VSpace"
import { PricingTypes } from "../../../common/constants"
import { useProposalPaymentScheduleQuery } from "./proposalsQueries"
import { buildRecurringPaymentText } from "../../Proposals/paymentScheduleModel"
import { hasValue } from "../../../helpers/nullable"
import { pluralize } from "../../../helpers/strings"
import dayjs from 'dayjs'
import { PaymentScheduleRow, PaymentScheduleOptions } from "../../../domain/proposals/settings/proposalSettings"

type Props = {
  latestProposalId: number
  workareas: Workarea[]
}

const ProposalPaymentSchedule = ({ latestProposalId, workareas }: Props) => {
  const proposalPaymentScheduleQuery = useProposalPaymentScheduleQuery(latestProposalId)
  const paymentScheduleOptions = proposalPaymentScheduleQuery.data

  if (proposalPaymentScheduleQuery.isLoading || !paymentScheduleOptions) {
    return <Collapse defaultActiveKey={1} style={{ marginBottom: '20px' }} items={[
      {
        key: 1,
        label: `Payment Schedule`,
        children: <Skeleton active />
      }
    ]} />
  }

  if (paymentScheduleOptions.useProgressBasedOnly) {
    const totalPriceWithoutSalesTax = calculateTotalPrice(workareas)
    const salesTax = calculateSalesTax(workareas)
    const totalPrice = totalPriceWithoutSalesTax + salesTax
    return <Collapse defaultActiveKey={1} style={{ marginBottom: '20px' }} items={[
      {
        key: 1,
        label: `Payment Schedule (${centsToDollarsDisplay(totalPrice)})`,
        children: <ProgressBased
          workareas={workareas}
          paymentSchedule={paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased}
        />
      }
    ]} />
  }

  const quotedAndTimeAndMaterialsWorkareas = workareas.filter(workarea => workarea.pricingType !== PricingTypes.PerVisit)
  const perVisitWorkareas = workareas.filter(workarea => workarea.pricingType === PricingTypes.PerVisit)
  const showSalesTax = workareas.some(workarea => workarea.estimatedSalesTax > 0)

  return <Collapse defaultActiveKey={1} style={{ marginBottom: '20px' }} items={[
    {
      key: 1,
      label: `Payment Schedule`,
      children: <VSpace size={"large"}>
        {quotedAndTimeAndMaterialsWorkareas.length > 0 && <VSpace>
          <strong>Quoted and Time & Materials</strong>
          {paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'progress-based' &&
            <ProgressBased
              workareas={quotedAndTimeAndMaterialsWorkareas}
              paymentSchedule={paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased}
            />
          }
          {paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'interval-based' &&
            <IntervalBasedSchedule
              paymentScheduleOptions={paymentScheduleOptions}
              workareas={quotedAndTimeAndMaterialsWorkareas}
              showSalesTax={showSalesTax}
            />
          }
        </VSpace>}
        
        {perVisitWorkareas.length > 0 && <VSpace>
          <strong>Per-Visit</strong>
          <PerVisitSchedule
            paymentScheduleOptions={paymentScheduleOptions}
            workareas={perVisitWorkareas}
            showSalesTax={showSalesTax}
          />
        </VSpace>}
      </VSpace>
    }
  ]} />
}

export default ProposalPaymentSchedule


type ProgressBasedProps = {
  workareas: Workarea[]
  paymentSchedule: PaymentScheduleRow[]
}

const ProgressBased = ({workareas, paymentSchedule} : ProgressBasedProps) => {
  const totalPriceWithoutSalesTax = calculateTotalPrice(workareas)
  const salesTax = calculateSalesTax(workareas)
  const totalPrice = totalPriceWithoutSalesTax + salesTax
  const updatedPaymentSchedule = updatePaymentScheduleAmounts(paymentSchedule, workareas, totalPriceWithoutSalesTax)
  const showEstimated = workareas.some(x => x.pricingType === PricingTypes.TimeAndMaterials || x.pricingType === PricingTypes.FlatRate)

  return <List
    footer={
      <span>
        <List.Item>
          <Row style={{ width: '100%' }}>
            <Col span={7} style={{ opacity: '80%' }}></Col>
            <Col span={1}></Col>
            <Col span={7}>{showEstimated ? 'Estimated' : ''} Total</Col>
            <Col span={1}></Col>
            <Col span={7}>{salesTax > 0
              ? <div>
                {centsToDollarsDisplay(totalPrice)}<br/>
                <Typography.Text type="secondary">Includes {centsToDollarsDisplay(salesTax)} sales tax</Typography.Text>
              </div>
              : centsToDollarsDisplay(totalPriceWithoutSalesTax)}
            </Col>
          </Row>
        </List.Item>
      </span>
    }
    locale={{ emptyText: <></> }}
    dataSource={updatedPaymentSchedule}
    renderItem={(schedule) => <List.Item>
      <Row style={{ width: '100%' }}>
        <Col span={7} style={{ opacity: '80%' }}>{schedule.note}</Col>
        <Col span={1}></Col>
        <Col span={7}>{schedule.title}</Col>
        <Col span={1}></Col>
        <Col span={7}>{showEstimated ? 'Estimated' : ''} {centsToDollarsDisplay(schedule.amount)} ({roundToPrecision(schedule.percentage * 100, 2)}%)</Col>
      </Row>
    </List.Item>
    }
  />
}

type IntervalBasedScheduleProps = {
  paymentScheduleOptions: PaymentScheduleOptions
  workareas: Workarea[]
  showSalesTax: boolean
}

const IntervalBasedSchedule = ({paymentScheduleOptions, workareas, showSalesTax}: IntervalBasedScheduleProps) => {
  const { perInvoiceAmount, salesTaxPerInvoice } = calculatePerInvoiceAmounts(workareas, paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments)
  const recurringPayment = paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.recurring
  const totalPayments = paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased.totalPayments
  const showEstimated = workareas.some(x => x.pricingType === PricingTypes.TimeAndMaterials || x.pricingType === PricingTypes.FlatRate)

  return <Row>
    <Col span={15}>
      <div>
        {buildRecurringPaymentText(recurringPayment)}
        {!recurringPayment.hideScheduleOnProposal && <>
          <br />
          <Typography.Text type="secondary">
            {recurringPayment.estimatedStartDate && `Starting ${dayjs(recurringPayment.estimatedStartDate).format('MMM D, YYYY')}`}
          </Typography.Text>
        </>}
      </div>
    </Col>
    <Col span={1}></Col>
    <Col span={7}>
      {hasValue(perInvoiceAmount) && <>
        <Typography.Text type="secondary">{totalPayments} {pluralize(totalPayments, 'payment')} of</Typography.Text> {showEstimated ? 'estimated' : ''} {centsToDollarsDisplay(perInvoiceAmount)}
        {showSalesTax && <>
          <br/>
          <Typography.Text type="secondary">Includes {centsToDollarsDisplay(salesTaxPerInvoice)} sales tax</Typography.Text>
        </>}
      </>}
    </Col>
  </Row>
}

type PerVisitScheduleProps = {
  paymentScheduleOptions: PaymentScheduleOptions
  workareas: Workarea[]
  showSalesTax: boolean
}

const PerVisitSchedule = ({paymentScheduleOptions, workareas, showSalesTax}: PerVisitScheduleProps) => {
  const recurringPayment = paymentScheduleOptions.perVisit.recurring

  return <>
    {workareas.map(workarea => <Row key={workarea.id} style={{marginBottom: '8px'}}>
      <Col span={15}>
        {workarea.name}
      </Col>
      <Col span={1}></Col>
      <Col span={7}>
        {centsToDollarsDisplay(workarea.quotedPrice + workarea.estimatedSalesTax)} per visit
        <br/>
        <Typography.Text type="secondary">
          {showSalesTax && `Includes ${centsToDollarsDisplay(workarea.estimatedSalesTax ?? 0)} sales tax`}
        </Typography.Text>
      </Col>
    </Row>)}
    {!recurringPayment.hideScheduleOnProposal && <Row>
      <Col span={15}>
        <div>
          {buildRecurringPaymentText(recurringPayment)}
          <br />
          <Typography.Text type="secondary">
            {recurringPayment.estimatedStartDate && `Starting ${dayjs(recurringPayment.estimatedStartDate).format('MMM D, YYYY')}`}
          </Typography.Text>
        </div>
      </Col>
      <Col span={8}></Col>
    </Row>}
  </>
}
